import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import { ScrollToPlugin } from 'gsap/dist/ScrollToPlugin'
import Container from '@material-ui/core/Container'
import ButtonCta from '@components/atoms/button-cta'
import CtaBox from '@components/cta-box/'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Video from '@components/atoms/video'
import {
  IconArrowDown, IconLongDownArrow, IconGoldPauseSmall, IconGoldPlaySmall,
} from '@components/atoms/icons'
import useStyles from './work-in-golf-hero.styles'

const WorkInGolfHero = (props) => {
  const {
    heroScrollCarouselCollection,
    heroVideoBackground,
    nextComponentID,
  } = props

  const {
    description, ctaBoxText, ctaBoxLinkText, ctaBoxUrl,
  } = heroScrollCarouselCollection.items[0]

  const videoBackground = heroVideoBackground?.contentType?.includes('video') && heroVideoBackground?.url

  const [playState, togglePlayState] = React.useState(true)
  const [isReducedPlayState, setReducedPlayState] = React.useState(false)
  const videoRef = useRef()
  const styles = useStyles()

  const handleVideoControlClick = (value) => {
    if (value === 'pause') {
      videoRef.current.pause()
      togglePlayState(false)
    } else {
      videoRef.current.play()
      togglePlayState(true)
    }
  }

  gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)

  useEffect(() => {
    setReducedPlayState(window.matchMedia('(prefers-reduced-motion: reduce)') === true || window.matchMedia('(prefers-reduced-motion: reduce)').matches === true)
  }, [])

  function skipToNextComponent() {
    gsap.to(window, { duration: 1, scrollTo: nextComponentID || '' })
  }

  return (
    <section className={styles.videoWrapper}>
      {videoBackground ? (
        <>
          <div className={styles.videoOverlay} />
          <Video
            src={videoBackground}
            autoPlay={!isReducedPlayState}
            muted
            loop
            playsInline
            className={styles.videoContainer}
            ref={videoRef}
          />
          <div className={styles.buttonContainer}>
            <button
              title={`${playState ? 'Pause' : 'Play'} background video`}
              type="button"
              className={`${styles.videoButton} ${playState ? styles.pauseButton : styles.playButton}`}
              onClick={() => handleVideoControlClick(playState ? 'pause' : 'play')}
            >
              <div className={styles.videoButtonIcon}>
                {playState ? <IconGoldPauseSmall /> : <IconGoldPlaySmall />}
              </div>
              <span className={styles.videoButtonLabel}>{`${playState ? 'Pause' : 'Play'} Video`}</span>
            </button>
          </div>
        </>
      ) : (
        <div className={styles.bgImagesContainer} aria-hidden="true">
          {heroScrollCarouselCollection?.items?.length > 0
            && heroScrollCarouselCollection.items.map((item, i) => (
              <div key={`${i + 1}`} className={styles.bgImageContainer}>
                <img className={`${styles.fvcBgImage} ${styles.fvcBgImage_selected}`} src={item.heroScrollCarouselBgImage.url} alt={item.heroScrollCarouselBgImage.description} />
              </div>
            ))}
        </div>
      )}

      <Button
        onClick={() => skipToNextComponent()}
        className={styles.skipLink}
        variant="text"
        color="primary"
        size="small"
      >
        Skip to Content
      </Button>

      <ul className={styles.sectionContainer}>
        {heroScrollCarouselCollection?.items?.length > 0
          && heroScrollCarouselCollection.items.map((item, i) => (
            <li key={`${i + 1}`} id={`slide${i}`} className={styles.slide}>
              <Container className={styles.slideContentContainer}>
                <div className={styles.slidePreHeading}>
                  {item.heroScrollCarouselHeadingPrecedingCopy}
                </div>
                <Typography variant="h2" variantMapping={{ h2: 'h1' }} className={styles.slideHeading}>
                  {item.heroScrollCarouselHeading}
                </Typography>
                <h3 className={styles.slideSubHeading}>{description}</h3>
                <Box align="center" className={styles.buttonCtaContainer}>
                  <div className={styles.iconLongArrow} aria-hidden>
                    <IconLongDownArrow className={styles.longDownArrow} alt="" />
                  </div>
                  <ButtonCta
                    variant="contained"
                    color="secondary"
                    href={item.heroScrollCarouselCtaLink}
                    className={styles.slideButtonCTA}
                  >
                    {item.heroScrollCarouselButtonLabel}
                  </ButtonCta>
                </Box>
                <div className={styles.ctaBoxWrapper}>
                  <CtaBox
                    ctaBoxText={ctaBoxText}
                    ctaBoxLinkText={ctaBoxLinkText}
                    ctaBoxUrl={ctaBoxUrl}
                  />
                </div>
              </Container>
            </li>
          ))}
      </ul>

      <div className={styles.halfCircle} aria-hidden>
        <IconArrowDown className={styles.halfCircleArrow} />
      </div>

    </section>
  )
}

WorkInGolfHero.propTypes = {
  heroScrollCarouselCollection: PropTypes.object,
  nextComponentID: PropTypes.string,
  description: PropTypes.string,
  ctaBoxText: PropTypes.string,
  ctaBoxLinkText: PropTypes.string,
  ctaBoxUrl: PropTypes.string,
  heroVideoBackground: PropTypes.object,
}

WorkInGolfHero.defaultProps = {}

export default WorkInGolfHero
